import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { getOpportunities, getOpportunityLinks } from '../../api/url_data_agent_evaluations';
import { formatNumber } from '../../helpers/url_data_agent_evaluations_helpers';
import {
  OPPORTUNITY_SORT_OPTIONS,
  getOpportunityHoldReasonCopy,
  getOpportunityWindow,
  sortOpportunities
} from '../../helpers/url_data_agent_opportunities_helpers';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';
import SilentEvalOpportunityLinks from './SilentEvalOpportunityLinks';

import './SilentEvalOpportunities.scss';

const SEARCH_DEBOUNCE_MS = 300;
const URL_DEFAULTS = { Opportunity_id: '', oq: '', osort: 'ending' };
const COUNT_COLUMNS = [
  { key: 'total', label: 'Links' },
  { key: 'open', label: 'Open' },
  { key: 'held', label: 'Held' },
  { key: 'awaiting', label: 'Awaiting' },
  { key: 'notRun', label: 'Not checked' },
  { key: 'running', label: 'Checking' },
  { key: 'agentMatched', label: 'APM matched' },
  { key: 'personMatched', label: 'Person matched' },
  { key: 'dismissed', label: 'Dismissed' }
];
const COUNTS_FAILED_NOTE = 'Counting this opportunity’s links took too long on the database. Open it to try again.';

// Live and recent opportunities with how far matching has come on each; opening one lists its links.
const SilentEvalOpportunities = ({ active = true }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const Opportunity_id = parseOpportunityId(urlState.Opportunity_id);
  const sort = OPPORTUNITY_SORT_OPTIONS.some(option => option.value === urlState.osort) ? urlState.osort : URL_DEFAULTS.osort;
  const search = urlState.oq;
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [result, setResult] = useState(null);
  const [countsById, setCountsById] = useState({});
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const countsRef = useRef({});
  const isListOnScreen = active && !Opportunity_id;
  const current = result?.search === debouncedSearch ? result : null;
  const opportunities = sortOpportunities(
    (current?.opportunities || []).map(opportunity => ({ ...opportunity, ...countsById[opportunity.id] })),
    sort
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isListOnScreen) return undefined;
    let cancelled = false;
    setIsFetching(true);
    setError(null);
    getOpportunities({ search: debouncedSearch || undefined })
      .then(data => {
        if (!cancelled) setResult({ ...data, search: debouncedSearch });
      })
      .catch(err => {
        if (!cancelled) setError(err || 'Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isListOnScreen, debouncedSearch, refreshKey]);

  // Each row's counts walk its participants' pins on the replica, so they load one opportunity at a time
  // after the list is on screen; a row whose walk is cut off shows a dash until it is opened.
  useEffect(() => {
    if (!isListOnScreen || !current) return undefined;
    const pending = current.opportunities.map(opportunity => opportunity.id).filter(id => !countsRef.current[id]);
    if (!pending.length) return undefined;
    let cancelled = false;
    const record = (id, counts) => {
      countsRef.current = { ...countsRef.current, [id]: counts };
      setCountsById(countsRef.current);
    };
    pending.forEach(id => record(id, { status: 'loading' }));
    (async () => {
      for (const id of pending) {
        if (cancelled) return;
        try {
          const data = await getOpportunityLinks(id, { countOnly: 1 });
          if (!cancelled) record(id, { status: 'ready', links: data.links, holdReasons: data.holdReasons });
        } catch {
          if (!cancelled) record(id, { status: 'failed' });
        }
      }
    })();
    return () => {
      cancelled = true;
      pending.filter(id => countsRef.current[id]?.status === 'loading').forEach(id => record(id, undefined));
    };
  }, [isListOnScreen, current]);

  const refresh = () => {
    countsRef.current = {};
    setCountsById({});
    setRefreshKey(key => key + 1);
  };

  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      const data = await getOpportunities({ search: debouncedSearch || undefined, offset: current.opportunities.length });
      setResult(previous => (previous?.search === debouncedSearch ? { ...previous, ...data, opportunities: [...previous.opportunities, ...data.opportunities] } : previous));
    } catch (err) {
      window.ALERT.error(`Could not load more opportunities: ${err || 'unknown error'}`);
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (Opportunity_id) {
    return <SilentEvalOpportunityLinks Opportunity_id={Opportunity_id} active={active} onBack={() => patchUrl({ Opportunity_id: '', linkState: '' })} />;
  }

  return (
    <div className='silent-eval-opportunities'>
      <div className='se-toolbar'>
        <input
          type='search'
          className='opp-search'
          value={search}
          placeholder='Search opportunity, brand or ID'
          aria-label='Search opportunities'
          onChange={event => patchUrl({ oq: event.target.value })}
        />
        <select className='opp-sort' aria-label='Sort' value={sort} onChange={event => patchUrl({ osort: event.target.value })}>
          {OPPORTUNITY_SORT_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button type='button' className='se-btn' disabled={isFetching} onClick={refresh}>
          Refresh
        </button>
        <span className={cn('se-refreshing', { on: isFetching })} aria-hidden='true' />
      </div>
      <p className='opp-intro'>
        Opportunities that are live or ended in the last 30 days. Links are the participants’ pins on the sites the opportunity counts; every link is
        checked by APM automatically, and the ones it leaves to people are finished here. Link counts fill in one opportunity at a time.
      </p>

      {error && (
        <div className='opp-error' role='alert'>
          Could not {current ? 'refresh' : 'load'} opportunities — {String(error)}
        </div>
      )}
      {!current ? (
        !error && (
          <div className='se-empty' role='status'>
            Loading opportunities…
          </div>
        )
      ) : !opportunities.length ? (
        <div className='se-empty'>{debouncedSearch ? 'No live or recent opportunity matches this search.' : 'No live or recent opportunities.'}</div>
      ) : (
        <div className='opp-list' role='table' aria-label='Opportunities'>
          <div className='opp-head' role='row'>
            <span role='columnheader'>Opportunity</span>
            <span role='columnheader'>Window</span>
            <span role='columnheader'>Participants</span>
            {COUNT_COLUMNS.map(column => (
              <span key={column.key} role='columnheader'>
                {column.label}
              </span>
            ))}
            <span role='columnheader'>Top hold reasons</span>
          </div>
          {opportunities.map(opportunity => {
            const tracking = getOpportunityWindow(opportunity);
            const counts = opportunity.status === 'ready' ? opportunity.links : null;
            return (
              <button
                key={opportunity.id}
                type='button'
                className='opp-row'
                role='row'
                aria-label={`Open opportunity ${opportunity.id}`}
                aria-busy={opportunity.status === 'loading'}
                onClick={() => patchUrl({ Opportunity_id: opportunity.id })}
              >
                <span className='opp-cell opp-name' role='cell'>
                  <span className='opp-title'>{opportunity.title || `Opportunity ${opportunity.id}`}</span>
                  <span className='opp-sub'>
                    {opportunity.brandName || 'Unknown brand'} · #{opportunity.id}
                  </span>
                </span>
                <span className='opp-cell' role='cell'>
                  <span>{tracking.range}</span>
                  <span className={cn('opp-sub', 'opp-window', tracking.status)}>{tracking.label}</span>
                </span>
                <span className='opp-cell num' role='cell'>
                  {formatNumber(opportunity.participantCount)}
                </span>
                {COUNT_COLUMNS.map(column => (
                  <span
                    key={column.key}
                    className={cn('opp-cell num', { zero: counts && !counts[column.key], open: column.key === 'open', pending: !counts })}
                    role='cell'
                    title={opportunity.status === 'failed' ? COUNTS_FAILED_NOTE : undefined}
                  >
                    {counts ? formatNumber(counts[column.key]) : opportunity.status === 'failed' ? '—' : '…'}
                  </span>
                ))}
                <span className='opp-cell opp-reasons' role='cell'>
                  {(opportunity.holdReasons || []).map(({ reason, count }) => (
                    <span key={reason} className='opp-reason' title={getOpportunityHoldReasonCopy(reason)}>
                      {formatNumber(count)} {getOpportunityHoldReasonCopy(reason)}
                    </span>
                  ))}
                </span>
              </button>
            );
          })}
          {current.hasMore && (
            <button type='button' className='se-btn opp-load-more' disabled={isLoadingMore} onClick={loadMore}>
              {isLoadingMore ? 'Loading…' : `Load more (${formatNumber(current.total - current.opportunities.length)} left)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const parseOpportunityId = value => {
  const normalized = String(value || '').trim();
  const Opportunity_id = Number(normalized);
  return /^\d+$/.test(normalized) && Number.isSafeInteger(Opportunity_id) && Opportunity_id > 0 ? Opportunity_id : null;
};

SilentEvalOpportunities.propTypes = {
  active: PropTypes.bool
};

export default SilentEvalOpportunities;
